package one.healthium.life.presentation.feature.today.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import ifaandroid.apps.life.composeapp.generated.resources.Res
import ifaandroid.apps.life.composeapp.generated.resources.ic_barbecue_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_bottle_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_clipboard_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_compass_20
import ifaandroid.apps.life.composeapp.generated.resources.ic_time_6
import loco.LIFE_TODAY_DAILY_TASKS_LOG_FOOD_LABEL_FORMAT
import loco.LIFE_TODAY_DAILY_TASKS_READ_INSIGHT_LABEL_FORMAT
import one.healthium.base.utils.relativeDate
import one.healthium.loco.resolve
import one.healthium.tasks.domain.DailyTaskType
import one.healthium.today.ui.models.DailyTaskTypedPayload
import one.healthium.today.ui.models.DailyTaskUiModel
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview

@Composable
internal fun TodayDailyTasks(
    modifier: Modifier = Modifier,
    dailyTasks: List<DailyTaskUiModel>,
    onClick: (DailyTaskUiModel) -> Unit,
) {
    LazyRow(
        modifier = modifier.fillMaxWidth(),
        contentPadding = PaddingValues(horizontal = 24.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        items(dailyTasks.size) { index ->
            val dailyTaskModel = dailyTasks.getOrNull(index) ?: return@items

            DailyTaskItem(
                model = dailyTaskModel,
                onClick = { onClick(dailyTaskModel) }
            )
        }
    }
}

@Composable
private fun DailyTaskItem(model: DailyTaskUiModel, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .width(200.dp)
            .height(IntrinsicSize.Min)
            .defaultMinSize(minHeight = 124.dp)
            .background(AppTheme.colors.primary, shape = AppTheme.shapes.small)
            .padding(horizontal = 12.dp, vertical = 16.dp)
            .clickable(onClick = onClick),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                painter = painterResource(model.type.toIcon()),
                tint = white,
                contentDescription = null,
            )
            Spacer(Modifier.width(6.dp))
            Text(
                text = model.name,
                style = AppTheme.textStyles.secondary_16_400,
                color = AppTheme.colors.onPrimary,
            )
        }
        Spacer(Modifier.height(8.dp))

        val payloadLabel = model.toPayloadLabel()
        if (payloadLabel != null) {
            Text(
                modifier = Modifier
                    .background(color = AppTheme.colors.secondary, shape = RoundedCornerShape(4.dp))
                    .padding(horizontal = 12.dp, vertical = 4.dp),
                text = payloadLabel,
                style = AppTheme.textStyles.secondary_9_400,
                color = AppTheme.colors.onSecondary
            )
        }
        Spacer(Modifier.weight(1f))

        val dateLabel = model.toDateLabel()
        if (dateLabel != null) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Image(
                    painter = painterResource(Res.drawable.ic_time_6),
                    contentDescription = null,
                )
                Spacer(Modifier.width(4.dp))
                Text(
                    text = dateLabel,
                    style = AppTheme.textStyles.secondary_9_400,
                    color = AppTheme.colors.onPrimary,
                )
            }
        }
    }
}

private fun DailyTaskType.toIcon(): DrawableResource = when (this) {
    is DailyTaskType.DoWorkout -> Res.drawable.ic_compass_20
    DailyTaskType.LogFood -> Res.drawable.ic_barbecue_20
    DailyTaskType.LogSymptoms -> Res.drawable.ic_compass_20
    DailyTaskType.LogWater -> Res.drawable.ic_bottle_20
    DailyTaskType.LogWeight -> Res.drawable.ic_compass_20
    is DailyTaskType.ReadInsight -> Res.drawable.ic_clipboard_20
}

@Composable
private fun DailyTaskUiModel.toPayloadLabel(): String? {
    return when (val payload = typedPayload) {
        is DailyTaskTypedPayload.LogFood -> stringResource(
            resource = loco.Res.string.LIFE_TODAY_DAILY_TASKS_LOG_FOOD_LABEL_FORMAT,
            "${payload.completed} / ${payload.overall}"
        )

        is DailyTaskTypedPayload.LogWater -> payload.todayWaterIntake?.displayValueWithUnit
        is DailyTaskTypedPayload.LogWeight -> payload.latestWeightLog?.displayValue?.resolve()
        is DailyTaskTypedPayload.ReadInsight -> stringResource(
            resource = loco.Res.string.LIFE_TODAY_DAILY_TASKS_READ_INSIGHT_LABEL_FORMAT,
            payload.insightsCount.toString()
        )

        null -> null
    }
}

@Composable
private fun DailyTaskUiModel.toDateLabel(): String? {
    return when (val payload = typedPayload) {
        is DailyTaskTypedPayload.LogFood -> payload.latestFoodLog?.date?.relativeDate()?.resolve()?.capFirstLetter()
        is DailyTaskTypedPayload.LogWater -> payload.latestWaterLog?.date?.relativeDate()?.resolve()?.capFirstLetter()
        is DailyTaskTypedPayload.LogWeight -> payload.latestWeightLog?.date?.relativeDate()?.resolve()?.capFirstLetter()
        is DailyTaskTypedPayload.ReadInsight -> payload.latestReadDate?.relativeDate()?.resolve()?.capFirstLetter()
        null -> null
    }
}

fun String.capFirstLetter(): String {
    return replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
}

@Composable
@Preview
private fun DailyTaskItemPreview() {
    AppTheme(Themes.life()) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            DailyTaskItem(DailyTaskUiModel.previewLogWeight, {})
            DailyTaskItem(DailyTaskUiModel.previewLogWater, {})
        }
    }
}